import { CompanyLinkType } from '@api';
import { ActionsCell, ContactDetails, PersonAnchor } from '@common';
import { faCheck, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Text } from '@mantine/core';
import { DataTableColumn } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';

const TRANS_PATH = 'COMPANY_EMPLOYEES.TABLE';

type UseCompanyEmployeesTableColumnsPropsType = {
  onDeleteEmployee: (companyLinkId: string) => void;
  onEditEmployee: (employee: CompanyLinkType) => void;
};

type UseCompanyEmployeesTableColumnsReturnType = {
  columns: DataTableColumn<CompanyLinkType>[];
};

export const useCompanyEmployeesTableColumns = ({
  onDeleteEmployee,
  onEditEmployee,
}: UseCompanyEmployeesTableColumnsPropsType): UseCompanyEmployeesTableColumnsReturnType => {
  const { t } = useTranslation('companyDetailsEmployees');

  const noDataPlaceholder = t('NO_DATA_PLACEHOLDER', { ns: 'common' });

  const columns: DataTableColumn<CompanyLinkType>[] = [
    {
      accessor: 'firstname',
      title: t(`${TRANS_PATH}.COLUMNS.FIRSTNAME`),
      width: 100,
      render: ({ firstname, personId }) => (
        <PersonAnchor
          person={{ type: 'PERSON', id: personId, name: firstname }}
        />
      ),
    },
    {
      accessor: 'lastname',
      title: t(`${TRANS_PATH}.COLUMNS.LASTNAME`),
      width: 100,
      render: ({ lastname, personId }) => (
        <PersonAnchor
          person={{ type: 'PERSON', id: personId, name: lastname }}
        />
      ),
    },
    {
      accessor: 'jobTitle',
      title: t(`${TRANS_PATH}.COLUMNS.JOB_TITLE`),
      render: ({ jobTitle }) => jobTitle ?? noDataPlaceholder,
      width: 200,
    },
    {
      accessor: 'contact',
      title: t(`${TRANS_PATH}.COLUMNS.CONTACT_DETAILS`),
      render: ({ contact }) => <ContactDetails contact={contact} />,
      width: 300,
    },
    {
      accessor: 'categories',
      title: t(`${TRANS_PATH}.COLUMNS.TAGS`),
      render: ({ categories }) => {
        if (!categories.length) return noDataPlaceholder;

        return categories
          .map((category) =>
            t(`COMPANY_LINKS.CATEGORIES.${category}`, {
              ns: 'common',
            })
          )
          .join(', ');
      },
      width: 200,
    },
    {
      accessor: 'excludeFromConfirmationMails',
      title: t(`${TRANS_PATH}.COLUMNS.EXCLUDE_FROM_CONFIRMATION_MAILS`),
      titleStyle: { textAlign: 'center' },
      render: ({ excludeFromConfirmationMails }) => (
        <Text ta="center">
          <FontAwesomeIcon
            icon={excludeFromConfirmationMails ? faCheck : faTimes}
          />
        </Text>
      ),
      width: 200,
    },
    {
      accessor: 'actions',
      title: t(`${TRANS_PATH}.COLUMNS.ACTIONS`),
      titleStyle: { textAlign: 'center' },
      render: (record) => (
        <ActionsCell.Root>
          <ActionsCell.Edit onClick={() => onEditEmployee(record)} />
          <ActionsCell.Delete onClick={() => onDeleteEmployee(record.linkId)} />
        </ActionsCell.Root>
      ),
      width: 100,
    },
  ];

  return { columns };
};
